import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindowF,
} from "@react-google-maps/api";
import Modal from "../Modal";
import Button from "../Button";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  actionGetAlertasByUF,
  actionResetGetAlertasByUF,
} from "../../features/dashboard-geral/alertas-by-uf/slices";
import "./styles.scss";
import { getUser } from "../../helpers/user";

const center = { lat: -14.235, lng: -51.9253 };

const estilosClaro = [
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#e9e9e9" }],
  },
  { featureType: "landscape", stylers: [{ color: "#ffffff" }] },
  {
    featureType: "administrative.country",
    elementType: "labels",
    stylers: [{ visibility: "on" }],
  },
  {
    featureType: "administrative.province",
    elementType: "labels",
    stylers: [{ visibility: "on" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
];

const estilosEscuro = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ color: "#757575" }, { visibility: "off" }],
  },
  {
    featureType: "administrative.country",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9e9e9e" }],
  },
  {
    featureType: "administrative.province",
    elementType: "labels.text.fill",
    stylers: [{ color: "#bdbdbd" }],
  },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#2c2c2c" }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#3a3a3a" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#3c3c3c" }],
  },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#111625" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#3d3d3d" }],
  },
];

const GEOJSON_URL =
  "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson";

type AgruparPorType = "UF" | "BR";

const centroEstadosBR: {
  [key: string]: { lat: number; lng: number; zoom: number };
} = {
  AC: { lat: -9.0238, lng: -70.812, zoom: 8 },
  AL: { lat: -9.5713, lng: -36.782, zoom: 10 },
  AM: { lat: -3.4168, lng: -64.183, zoom: 7 },
  AP: { lat: 1.4116, lng: -51.77, zoom: 8 },
  BA: { lat: -12.9714, lng: -38.5014, zoom: 8 },
  CE: { lat: -3.7174, lng: -38.5434, zoom: 9 },
  DF: { lat: -15.7801, lng: -47.9292, zoom: 11 },
  ES: { lat: -19.1834, lng: -40.3089, zoom: 9 },
  GO: { lat: -16.6869, lng: -49.2648, zoom: 8 },
  MA: { lat: -5.4241, lng: -45.4108, zoom: 8 },
  MG: { lat: -19.9167, lng: -43.9345, zoom: 8 },
  MS: { lat: -20.7722, lng: -54.7852, zoom: 8 },
  MT: { lat: -12.6819, lng: -56.9211, zoom: 7 },
  PA: { lat: -3.7931, lng: -52.3963, zoom: 7 },
  PB: { lat: -7.24, lng: -36.782, zoom: 9 },
  PE: { lat: -8.0543, lng: -34.8813, zoom: 9 },
  PI: { lat: -7.5028, lng: -42.8229, zoom: 8 },
  PR: { lat: -24.89, lng: -51.55, zoom: 9 },
  RJ: { lat: -22.35, lng: -43.15, zoom: 9 },
  RN: { lat: -5.795, lng: -36.568, zoom: 9 },
  RO: { lat: -10.83, lng: -63.34, zoom: 8 },
  RR: { lat: 2.1197, lng: -61.3831, zoom: 7 },
  RS: { lat: -29.7, lng: -53.35, zoom: 8 },
  SC: { lat: -27.25, lng: -50.2, zoom: 9 },
  SE: { lat: -10.5741, lng: -37.3853, zoom: 10 },
  SP: { lat: -22.25, lng: -48.5, zoom: 8 },
  TO: { lat: -10.2495, lng: -48.3242, zoom: 8 },
};

const centroBR = { lat: -14.235, lng: -51.9253, zoom: 5 };

const ZOOM_THRESHOLD = 7;

const ModalAlertas = ({ uf, close }) => {
  const dispatch = useAppDispatch();
  const { getAlertasByUF } = useAppSelector((state) => state);

  useEffect(() => {
    dispatch(actionResetGetAlertasByUF());
    if (uf) dispatch(actionGetAlertasByUF(uf.toLowerCase()));
  }, [uf, dispatch]);

  return (
    <Modal
      size="md"
      header={<h3 className="heading--md">Lojas - {uf.toUpperCase()}</h3>}
      overflow={true}
      actions={
        <Button size="sm" variant="primary" onClick={close}>
          Fechar
        </Button>
      }
    >
      <div className="modal-list-alert">
        {getAlertasByUF.status === "loading" && <strong>carregando...</strong>}
        {getAlertasByUF.data.map(
          ({ idLoja, descricao, numero, nome, acesso, alerta }) => (
            <Link
              className={`modal-list-alert__item ${!acesso && "disabled"} ${alerta && "with-alert"}`}
              to={`/pontos-de-monitoramento/alertas/${idLoja}`}
              key={numero}
            >
              <p>
                {numero} - {nome} {descricao && `: ${descricao}`}
              </p>
            </Link>
          ),
        )}
      </div>
    </Modal>
  );
};

const MapaGoogle = ({
  status,
  lojasMapa,
  fillScreen = false,
  zoom: initialZoom,
  tipoMapa = "roadmap",
  tema = "light",
  agrupar = false,
  agruparPor = "UF",
  height = "600px",
}: {
  status: any;
  lojasMapa: any[];
  fillScreen?: boolean;
  zoom: number;
  tipoMapa?: "roadmap" | "satellite" | "hybrid" | "terrain";
  tema?: "light" | "dark";
  agrupar?: boolean;
  agruparPor?: AgruparPorType;
  /** Altura do container quando fillScreen=false. Aceita qualquer valor CSS
   * válido ("600px", "100%", "calc(100vh - 200px)"...). Use "100%" quando o
   * componente pai já controla a altura disponível via flex/grid. */
  height?: string;
}) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY || "",
    language: "pt-BR",
    region: "BR",
  });

  const user = getUser();
  const nivelUsuario = user?.usuario?.nivel;

  const containerStyle: React.CSSProperties = {
    width: fillScreen ? "100vw" : "100%",
    height: fillScreen ? "100vh" : height,
    position: fillScreen ? "fixed" : "relative",
    top: fillScreen ? 0 : "auto",
    left: fillScreen ? 0 : "auto",
    zIndex: fillScreen ? 999 : 1,
    borderRadius: fillScreen ? "0px" : "8px",
    // Impede que o scroll da página "vaze" pro mapa (ou vice-versa) durante
    // um gesto de pan/zoom em andamento.
    overscrollBehavior: "contain",
  };

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const [ufSelecionada, setUfSelecionada] = useState("");
  const [hoverLoja, setHoverLoja] = useState<any>(null);

  const lojasFiltradas = useMemo(() => {
    if (!lojasMapa) return [];
    return lojasMapa.filter((loja) => {
      const temCoordenadas = Number(loja.latitude) && Number(loja.longitude);
      const nivelOk = nivelUsuario === 9 || loja.statusCentral === 1;
      return temCoordenadas && nivelOk;
    });
  }, [lojasMapa, nivelUsuario]);

  const dadosAgrupadosPorUF = useMemo(() => {
    if (!agrupar || agruparPor !== "UF") return [];

    const agrupado: {
      [key: string]: { uf: string; qtd: number; temAlerta: boolean };
    } = {};
    lojasFiltradas.forEach((loja) => {
      const ufKey = String(loja.uf).toUpperCase().trim();
      if (ufKey && ufKey !== "NULL" && ufKey !== "UNDEFINED") {
        if (!agrupado[ufKey])
          agrupado[ufKey] = { uf: ufKey, qtd: 0, temAlerta: false };
        agrupado[ufKey].qtd += 1;
        if (loja.status === 2) agrupado[ufKey].temAlerta = true;
      }
    });
    return Object.values(agrupado);
  }, [lojasFiltradas, agrupar, agruparPor]);

  const dadosAgrupadosBR = useMemo(() => {
    if (!agrupar || agruparPor !== "BR") return null;

    const qtd = lojasFiltradas.length;
    const temAlerta = lojasFiltradas.some((l) => l.status === 2);
    return { qtd, temAlerta };
  }, [lojasFiltradas, agrupar, agruparPor]);

  const zoomAtual = map ? (map.getZoom() ?? currentZoom) : currentZoom;
  const emVisaoMacro = agrupar && zoomAtual <= ZOOM_THRESHOLD;

  const getColorByStatus = useCallback(
    (siglaUf: string) => {
      if (!status) return "#a6a6a6";
      const estadoStatus = status[siglaUf.toLowerCase()];
      if (!estadoStatus) return "#a6a6a6";
      const s = String(estadoStatus).toUpperCase();
      if (s.includes("ALERTA_INCENDIO")) return "#d54d59";
      if (s.includes("ALERTA_OUTROS") || s.includes("ALERTA_MANUTENCAO"))
        return "#e5bb4b";
      if (s.includes("OK") || s.includes("ACTIVE")) return "#40bc9a";
      return "#a6a6a6";
    },
    [status],
  );

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance);
      setCurrentZoom(mapInstance.getZoom() || initialZoom);

      mapInstance.data.loadGeoJson(GEOJSON_URL);
      mapInstance.data.setStyle((feature) => {
        const sigla = feature.getProperty("sigla") as string;
        return {
          fillColor: getColorByStatus(sigla || ""),
          fillOpacity: 0.35,
          strokeColor: "#ffffff",
          strokeWeight: 1.5,
          clickable: true,
        };
      });

      mapInstance.data.addListener("click", (event) => {
        const sigla = event.feature.getProperty("sigla") as string;
        if (sigla) {
          const foco = centroEstadosBR[sigla.toUpperCase()];
          if (foco) {
            mapInstance.setCenter({ lat: foco.lat, lng: foco.lng });
            mapInstance.setZoom(foco.zoom);
          } else {
            setUfSelecionada(sigla);
          }
        }
      });

      mapInstance.data.addListener("mouseover", (event) => {
        mapInstance.data.overrideStyle(event.feature, {
          fillOpacity: 0.65,
          strokeWeight: 2.5,
        });
      });
      mapInstance.data.addListener("mouseout", () => {
        mapInstance.data.revertStyle();
      });
    },
    [getColorByStatus, initialZoom],
  );

  useEffect(() => {
    if (map) {
      map.data.setStyle((feature) => {
        const sigla = feature.getProperty("sigla") as string;
        return {
          fillColor: getColorByStatus(sigla || ""),
          fillOpacity: 0.4,
          strokeColor: tema === "dark" ? "#222" : "#ffffff",
          strokeWeight: 1.5,
        };
      });
    }
  }, [map, status, getColorByStatus, tema]);

  const onUnmount = useCallback(() => setMap(null), []);

  // ─── Proteções contra o mapa ficar "preso" arrastando ───
  // Se o mouseup acontecer em algo que não dispara evento de DOM (ex: a
  // scrollbar nativa do navegador) o Google Maps nunca recebe o sinal de
  // "soltou o botão" e continua tratando qualquer movimento do mouse como
  // pan. Esse listener global funciona como rede de segurança: qualquer
  // mouseup ou perda de foco da janela força o mapa a descartar o estado
  // de drag.
  useEffect(() => {
    if (!map) return;

    const forceStopDrag = () => {
      map.setOptions({ draggable: false });
      map.setOptions({ draggable: true });
      document.body.style.overflow = "";
    };

    window.addEventListener("mouseup", forceStopDrag);
    window.addEventListener("blur", forceStopDrag);

    return () => {
      window.removeEventListener("mouseup", forceStopDrag);
      window.removeEventListener("blur", forceStopDrag);
    };
  }, [map]);

  if (!isLoaded) return <div className="loading-map">Carregando Mapa...</div>;

  return (
    <>
      {ufSelecionada && (
        <ModalAlertas uf={ufSelecionada} close={() => setUfSelecionada("")} />
      )}

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={initialZoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        mapTypeId={tipoMapa}
        onZoomChanged={() => {
          if (map) setCurrentZoom(map.getZoom() || initialZoom);
          if (hoverLoja) setHoverLoja(null);
        }}
        onDragStart={() => {
          if (hoverLoja) setHoverLoja(null);
          // Trava o scroll da página enquanto o drag está ativo, pra evitar
          // que o container do mapa se desloque na tela no meio do gesto
          // (o que faz o cálculo de pan do Maps ficar errado).
          document.body.style.overflow = "hidden";
        }}
        onDragEnd={() => {
          document.body.style.overflow = "";
        }}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          // "greedy" faz o mapa capturar pan/zoom de forma exclusiva e
          // previsível, sem depender de heurística de scroll da página.
          gestureHandling: "greedy",
          styles:
            tipoMapa !== "roadmap"
              ? []
              : tema === "dark"
                ? estilosEscuro
                : estilosClaro,
        }}
      >
        {emVisaoMacro &&
          agruparPor === "UF" &&
          dadosAgrupadosPorUF.map((item) => {
            const coords = centroEstadosBR[item.uf];
            if (!coords) return null;
            return (
              <MarkerF
                key={`uf-${item.uf}`}
                position={{ lat: coords.lat, lng: coords.lng }}
                onClick={() => {
                  if (map) {
                    map.setCenter({ lat: coords.lat, lng: coords.lng });
                    map.setZoom(coords.zoom);
                  }
                }}
                label={{
                  text: String(item.qtd),
                  color: "#ffffff",
                  fontSize: "13px",
                  fontWeight: "700",
                }}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 18,
                  fillColor: item.temAlerta ? "#d54d59" : "#40bc9a",
                  fillOpacity: 0.95,
                  strokeWeight: 2.5,
                  strokeColor: "#ffffff",
                }}
              />
            );
          })}

        {emVisaoMacro && agruparPor === "BR" && dadosAgrupadosBR && (
          <MarkerF
            key="cluster-br"
            position={{ lat: centroBR.lat, lng: centroBR.lng }}
            onClick={() => {
              if (map) {
                map.setCenter({ lat: centroBR.lat, lng: centroBR.lng });
                map.setZoom(centroBR.zoom);
              }
            }}
            label={{
              text: String(dadosAgrupadosBR.qtd),
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: "700",
            }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 22,
              fillColor: dadosAgrupadosBR.temAlerta ? "#d54d59" : "#40bc9a",
              fillOpacity: 0.95,
              strokeWeight: 2.5,
              strokeColor: "#ffffff",
            }}
          />
        )}

        {!emVisaoMacro &&
          lojasFiltradas.map((loja) => {
            const lat = Number(loja.latitude);
            const lng = Number(loja.longitude);
            const colorMarker =
              loja.statusCentral === 0
                ? "#a0aec0"
                : loja.status === 2
                  ? "#d54d59"
                  : "#40bc9a";

            return (
              <MarkerF
                key={loja.id}
                position={{ lat, lng }}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: colorMarker,
                  fillOpacity: 1,
                  strokeWeight: 2,
                  strokeColor: "#ffffff",
                }}
                onClick={() => setUfSelecionada(loja.uf)}
                onMouseOver={() => setHoverLoja(loja)}
                onMouseOut={() => setHoverLoja(null)}
              />
            );
          })}

        {hoverLoja && !emVisaoMacro && (
          <InfoWindowF
            position={{
              lat: Number(hoverLoja.latitude),
              lng: Number(hoverLoja.longitude),
            }}
            options={{
              disableAutoPan: true,
              pixelOffset: new google.maps.Size(0, -10),
              zIndex: 9999,
            }}
            onCloseClick={() => setHoverLoja(null)}
          >
            <div className="custom-tooltip">
              <strong>{hoverLoja.nome}</strong>
              <small>{hoverLoja.endereco}</small>
              {hoverLoja.statusCentral === 0 ? (
                <span
                  className="status-badge alert"
                  style={{ backgroundColor: "#a0aec0" }}
                >
                  Offline
                </span>
              ) : (
                <span
                  className={`status-badge ${hoverLoja.status === 2 ? "alert" : "ok"}`}
                >
                  {hoverLoja.status === 2 ? "Alerta" : "Online"}
                </span>
              )}
              <span
                style={{
                  display: "block",
                  fontSize: "10px",
                  marginTop: "6px",
                  color: "#aaa",
                }}
              >
                Central:{" "}
                {hoverLoja.statusCentral === 1
                  ? "● Conectada"
                  : "○ Desconectada"}
              </span>
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>
    </>
  );
};

export default MapaGoogle;
